import { useReducer, useEffect } from 'react';

import { SpotifyArtist } from '../Spotify/spotifyClient.types';

import {
  State,
  Action,
  UseBackendLoginHook,
} from './useBackendLoginHook.types';
import { backendClientPublic } from './backendClient';

/**
 *
 */
const initialState: State = {
  allowOnboarding: false,
  allowSubmit: false,
  error: null,
  user: null,
  artists: null,
  genres: null,
  clubs: null,
};

const reducer = (state: State, action: Action) => {
  switch (action.type) {
    case 'SET_ARTISTS':
      return {
        ...state,
        artists: action.payload,
      };
    case 'SET_GENRES':
      return {
        ...state,
        genres: action.payload,
        allowSubmit: true,
      };
    case 'SET_CLUBS':
      return {
        ...state,
        clubs: action.payload,
        allowOnboarding: true,
      };
    case 'SET_USER':
      return {
        ...state,
        user: action.payload,
      };
    case 'SET_ERROR':
      return {
        ...state,
        error: action.payload,
      };
    default:
      return { ...state };
  }
};

export const useBackendLoginHook = ({ artists, user }: UseBackendLoginHook) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    if (user) {
      dispatch({
        type: 'SET_USER',
        payload: {
          spotifyId: user.id,
          image: (user.images && user.images[0]?.url) || '',
          email: user.email,
          product: user.product,
          type: user.type,
          uri: user.uri,
        },
      });
    }
  }, [user]);

  useEffect(() => {
    if (
      artists &&
      (artists.shortTerm || artists.mediumTerm || artists.longTerm)
    ) {
      dispatch({
        type: 'SET_ARTISTS',
        payload: {
          shortTerm: artists.shortTerm?.map((_) => _.name),
          mediumTerm: artists.mediumTerm?.map((_) => _.name),
          longTerm: artists.longTerm?.map((_) => _.name),
        },
      });

      let shortTerm = null,
        mediumTerm = null,
        longTerm = null;

      if (artists.shortTerm) {
        shortTerm = getGenres(artists.shortTerm);
      }
      if (artists.mediumTerm) {
        mediumTerm = getGenres(artists.mediumTerm);
      }
      if (artists.longTerm) {
        longTerm = getGenres(artists.longTerm);
      }

      dispatch({
        type: 'SET_GENRES',
        payload: { shortTerm, mediumTerm, longTerm },
      });
    }
  }, [artists]);

  useEffect(() => {
    if (state.allowSubmit) {
      const { user, artists, genres } = state;
      const register = async () => {
        try {
          const { clubs } = await backendClientPublic().register({
            user,
            artists,
            genres,
          });

          dispatch({
            type: 'SET_CLUBS',
            payload: clubs,
          });
        } catch (error) {
          if (error.isAxiosError) {
            return dispatch({
              type: 'SET_ERROR',
              payload: error.response?.data?.error?.message || error.message,
            });
          }

          throw error;
        }
      };

      register();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.allowSubmit]);

  return state;
};

const getGenres = (items: SpotifyArtist[]): [string, number][] => {
  const genres = new Map<string, number>();

  for (const artist of items) {
    for (const genre of artist.genres) {
      const existing = genres.get(genre);
      if (existing) {
        genres.set(genre, existing + 1);
      } else {
        genres.set(genre, 1);
      }
    }
  }

  return [...new Map([...genres.entries()].sort((a, b) => b[1] - a[1]))];
};
